/**
 * TYT 2.0 APP MODULE
 */

/* CORE MODULES */

import { HttpClientModule } from '@angular/common/http';
import { BrowserModule, BrowserTransferStateModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, Routes, PreloadAllModules, RouteReuseStrategy } from '@angular/router';
import { FreeAccountBenefitsComponent } from '@tytapp/app/free-account-benefits/free-account-benefits.component';
import { PrebootModule } from 'preboot';

/* THIRD PARTY */

import { Angulartics2Module } from 'angulartics2';
import { Angulartics2GoogleTagManager } from 'angulartics2/gtm';
import { LocalStorageModule } from 'angular-2-local-storage';
import { RecaptchaModule, RecaptchaFormsModule } from 'ng-recaptcha';

/* MISC */

import './init-ui';
import "../models";
import { ROUTES } from './routes';

/* DECLARATIONS */

import { AppErrorHandler } from './error-handler';
import { AppComponent } from './app.component';
export { AppComponent }

import { HomeComponent } from './home/home.component';
import { LogoutComponent } from './logout/logout.component';
import { JoinAfterLoginComponent } from './join-after-login/join-after-login.component';

/* FEATURES */

import { TYTCommonModule } from '../common';
import { ApiModule, ApiConfiguration } from '../api';
import { ModelsModule } from '../models';
import { UserModule } from '../user';
import { MediaPlaybackModule } from '../media-playback';
import { StripeModule } from '../stripe';
import { ValidationModule } from '../validation';
import { HostsModule } from '../hosts';

import { ApiConfigurationFactory } from './api-config';

import { CarouselComponent } from './carousel/carousel.component';
import { CarouselItemComponent } from './carousel-item/carousel-item.component';
import { MessageDialogComponent } from './message-dialog/message-dialog.component';
import { ServerLogger } from './server-logger';
import { NavComponent } from './nav/nav.component';
import { AppConfig } from '../common';
import { CommonUiModule } from 'common-ui/common-ui.module';
import { PluginsModule } from 'plugins/plugins.module';
import { PaymentConfirmationComponent } from './payment-confirmation/payment-confirmation.component';
import { MembershipBenefitsComponent } from './membership-benefits/membership-benefits.component';
import { HowToWatchComponent } from './how-to-watch/how-to-watch.component';
import { ProductionCalendarComponent } from './production-calendar/production-calendar.component';
import { WatchLiveComponent } from './watch-live/watch-live.component';
import { WatchPageComponent } from './watch-page/watch-page.component';
import { PodcastsComponent } from './podcasts/podcasts.component';
import { PodcastComponent } from './podcast/podcast.component';
import { RedirectionComponent } from './redirection/redirection.component';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environment';
import { CustomRouteReuseStrategy } from './route-reuse';
import { ForgotComponent } from './forgot/forgot.component';
import { ConfirmationRequiredComponent } from './confirmation-required/confirmation-required.component';
import { ConfirmAccountComponent } from './confirm-account/confirm-account.component';
import { SpotJumpComponent } from './spot-jump/spot-jump.component';
import { RedemptionComponent } from './redemption/redemption.component';
import { CastReceiverComponent } from './cast-receiver/cast-receiver.component';
import { FeedComponent } from './feed/feed.component';
import { FeedItemComponent } from './feed-item/feed-item.component';
import { TodaysLiveScheduleComponent } from './todays-live-schedule/todays-live-schedule.component';
import { JobsPageComponent } from './jobs-page/jobs-page.component';
import { TopicFeedPageComponent } from './topic-feed-page/topic-feed-page.component';
import { TopicFeedItemComponent } from './topic-feed-item/topic-feed-item.component';
import { RegistrationBenefitsComponent } from './registration-benefits/registration-benefits.component';
import { TopicsIndexComponent } from './topics-index/topics-index.component';
import { SettingsComponent } from './settings/settings.component';
import { FinishOauthSignupComponent } from './finish-oauth-signup/finish-oauth-signup.component';
import { OfflinePageComponent } from './offline-page/offline-page.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PollsModule } from '../polls';
import { PodcastItemComponent } from '@tytapp/app/podcasts/podcast-item/podcast-item.component';
import { AccountSettingsComponent } from './account-settings/account-settings.component';
import { CompanyImpactPageComponent } from './company-impact-page/company-impact-page.component';
import { CompanyImpactItemComponent } from './company-impact-item/company-impact-item.component';

@NgModule({
  declarations: [
    AppComponent,

    /**
     * DIALOGS ********************
     * - These must be added to entryComponents below.
     */

    JoinAfterLoginComponent,
    MessageDialogComponent,
    PaymentConfirmationComponent,
    MembershipBenefitsComponent,
    RegistrationBenefitsComponent,
    FreeAccountBenefitsComponent,
    ForgotComponent,
    ConfirmationRequiredComponent,
    ConfirmAccountComponent,
    HowToWatchComponent,

    /**
     * PAGES *********************
     */

    SettingsComponent,
    AccountSettingsComponent,
    HomeComponent,
    LogoutComponent,
    CarouselComponent,
    CarouselItemComponent,
    NavComponent,
    ProductionCalendarComponent,
    WatchLiveComponent,
    WatchPageComponent,
    PodcastsComponent,
    PodcastComponent,
    PodcastItemComponent,
    RedirectionComponent,
    SpotJumpComponent,
    RedemptionComponent,
    CastReceiverComponent,
    FeedComponent,
    FeedItemComponent,
    TopicFeedItemComponent,
    TodaysLiveScheduleComponent,
    TopicsIndexComponent,
    JobsPageComponent,
    TopicFeedPageComponent,
    FinishOauthSignupComponent,
    OfflinePageComponent,
    CompanyImpactPageComponent,
    CompanyImpactItemComponent
  ],

  /**
   * Entry components, must include any dialogs
   */
  entryComponents: [
    JoinAfterLoginComponent,
    MessageDialogComponent,
    ForgotComponent,
    ConfirmationRequiredComponent,
    ConfirmAccountComponent,
    RedemptionComponent
  ],

  imports: [
    /* CORE */

    ApiModule.forRoot(),
    BrowserTransferStateModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    RouterModule.forRoot(ROUTES, {
      //preloadingStrategy: PreloadAllModules
      anchorScrolling: 'disabled',
      scrollPositionRestoration: 'top',
      initialNavigation: 'enabled'
    }),

    BrowserModule.withServerTransition({ appId: 'tytapp' }),
    PrebootModule.withConfig({ appRoot: 'app-root', replay: false }),
    //ServerTransition.forRoot({ appId: 'tytapp' }),

    /* THIRD PARTY */

    Angulartics2Module.forRoot([Angulartics2GoogleTagManager]),
    LocalStorageModule.forRoot({
      prefix: 'tyt',
      storageType: 'localStorage'
    }),
    RecaptchaModule,
    RecaptchaFormsModule,

    /* FEATURES */

    StripeModule.forRoot(),
    TYTCommonModule.forRoot(),
    CommonUiModule.forRoot(),
    MediaPlaybackModule.forRoot(),
    UserModule.forRoot(),
    ModelsModule.forRoot(),
    ValidationModule.forRoot(),
    PollsModule.forRoot(),
    HostsModule.forRoot(),
    PluginsModule,
    ServiceWorkerModule.register('ngsw-worker.js', { enabled: environment.enableServiceWorker })

  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy
    },
    ServerLogger,
    { provide: ApiConfiguration,
      useFactory: ApiConfigurationFactory,
      deps: [AppConfig] },
    { provide: ErrorHandler,
      useClass: AppErrorHandler }
  ]
})
export class AppModule { }
